import axios from 'axios';
import { CONFIG_SETTINGS, USE_PROXY } from '../config';

/**
 * @module Utils/Axios
 * @description This file contains functions to handle Axios Request Manager
 *
 * En modo proxy (producción) las instancias NO llevan x-api-key: apuntan a
 * /api/onboarding y /api/services (mismo origen) y la Azure Function inyecta
 * el secreto server-side. En dev (localhost) sí se manda la key local.
 */

/**
 * Arma los headers de una instancia agregando la api-key solo si existe.
 * En modo proxy la key viene vacía, así no se envía un header vacío.
 */
const buildHeaders = (apiKey, extra = {}) => {
  const headers = { 'Content-Type': 'application/json', ...extra };
  if (apiKey) headers['x-api-key'] = apiKey;
  return headers;
};

/**
 * Azure Static Web Apps puede sobrescribir el header Authorization con su token
 * interno, por eso duplicamos el token del usuario en X-User-Authorization; el
 * proxy lo vuelve a mapear a Authorization antes de llamar al backend.
 */
const attachUserTokenPassthrough = (instance) => {
  if (!USE_PROXY) return instance;
  instance.interceptors.request.use((config) => {
    const auth =
      config.headers?.Authorization ||
      config.headers?.authorization ||
      config.headers?.common?.Authorization;
    if (auth) config.headers['X-User-Authorization'] = auth;
    return config;
  });
  return instance;
};

/////////////////////////// ONBOARDING SERVER  ///////////////////////////////////////

/* This code block is creating an instance of the Axios library with the name `_onboarding_server`. The
instance is configured with a base URL and headers that include a content type of JSON and an API
key. This instance can be used to make HTTP requests to the server specified in the `baseURL`
property. */
const _onboarding_server = attachUserTokenPassthrough(axios.create({
  baseURL: CONFIG_SETTINGS.ONBOARDING_URL,
  headers: buildHeaders(CONFIG_SETTINGS.ONBOARDING_APIKEY),
}))

/* This code block is creating an interceptor for the `_onboarding_server` axios instance. The
interceptor is used to handle errors that may occur during HTTP requests made with this instance. */
_onboarding_server.interceptors.response.use(
  (response) => response,
  (error) => {
    let error_data = {
      code:0,
      message:'There was an internal error, try again'
    }
    
    if(error?.response && error?.response?.data && error?.response?.data?.message){
      error_data.message = error?.response?.data?.message
      error_data.code = error?.response?.data?.code
    }else{
      console.log(error)
      if(error?.response?.status === 404){
        error_data = {
          code:404,
          message:'Request or server service not found'
        }
      }
      if(error?.response?.status === 401){
        error_data = {
          code:401,
          message:'Token has expired'
        }
      }
    }
    //return Promise.reject((error?.response && error?.response?.data) || 'Something went wrong')
    return Promise.reject(error_data)
  }
);

export const onboarding_server = _onboarding_server


/////////////////////////// ONBOARDING SERVER  ///////////////////////////////////////

/**  
 * @var {Axios}
 * @description This code block is creating an instance of the Axios library with the name `_services_server`. The 
 * instance is configured with a base URL and headers that include a content type of JSON and an API 
 * key. This instance can be used to make HTTP requests to the server specified in the `baseURL` 
 * property.
 */
const _services_server = attachUserTokenPassthrough(axios.create({
  baseURL: CONFIG_SETTINGS.SERVICES_API_URL,
  headers: buildHeaders(CONFIG_SETTINGS.API_APIKEY),
}))

/* This code block is creating an interceptor for the `_services_server` axios instance. The
interceptor is used to handle errors that may occur during HTTP requests made with this instance. If
an error occurs, the interceptor checks if the error response contains a `data` object with an
`error` property. If it does, the `error_data` variable is set to the value of the `error` property.
Otherwise, the `error_data` variable is set to a default error message. The interceptor then returns
a rejected Promise with the `error_data` object as the reason for rejection. */
_services_server.interceptors.response.use(
  (response) => response,
  (error) => {
    let error_data = {
      code:0,
      message:'There was an internal error, try again'
    }
    if(error?.response && error?.response?.data && error?.response?.data?.error){
      error_data = error?.response?.data?.error
    }
    //return Promise.reject((error?.response && error?.response?.data) || 'Something went wrong')
    return Promise.reject(error)
  }
);

export const services_server = _services_server

/////////////////////////// PUBLIC SERVER  ///////////////////////////////////////

/**  
 * @var {Axios}
 * @description This code block is creating an instance of the Axios library with the name `_public_server`. The
 * instance is configured with a base URL and headers that include a content type of JSON and an
 * Authorization header with a bearer token. This instance can be used to make HTTP requests to the
 * server specified in the `baseURL` property, with the specified headers included in the request.
 */


const _public_server = axios.create({
  baseURL: CONFIG_SETTINGS.API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization':CONFIG_SETTINGS.API_BEARER
  },
})

/* This code block is creating an interceptor for the `_public_server` axios instance. The interceptor
is used to handle errors that may occur during HTTP requests made with this instance. If an error
occurs, the interceptor checks if the error response contains a `data` object with an `error`
property. If it does, the `error_data` variable is set to the value of the `error` property.
Otherwise, the `error_data` variable is set to a default error message. The interceptor then returns
a rejected Promise with the `error_data` object as the reason for rejection. */
_public_server.interceptors.response.use(
  (response) => response,
  (error) => {
    let error_data = {
      code:0,
      message:'Hubo un error'
    }
    if(error?.response && error?.response?.data && error?.response?.data?.error){
      error_data = error?.response?.data?.error
    }
    //return Promise.reject((error?.response && error?.response?.data) || 'Something went wrong')
    return Promise.reject(error_data)
  }
);

export const public_server = _public_server

/////////////////////////// PRIVATE SERVER  ///////////////////////////////////////

/**  
 * @var {Axios}
 * @description This code block is creating an instance of the Axios library with the name `_private_server`. The
 * instance is configured with a base URL and headers that include a content type of JSON and a custom
 * header called `c2go-device-uuid` with a value of `CONFIG_SETTINGS.CMS_DEVICE_UUID`. This instance
 * can be used to make HTTP requests to the server specified in the `baseURL` property, with the
 * specified headers included in the request.
 */

const _private_server = axios.create({
  baseURL: CONFIG_SETTINGS.CMS_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'c2go-device-uuid':CONFIG_SETTINGS.CMS_DEVICE_UUID
  },
})

/* This code block is creating an interceptor for the `_private_server` axios instance. The interceptor
is used to handle errors that may occur during HTTP requests made with this instance. If an error
occurs, the interceptor checks if the error response contains a `data` object with an `error`
property. If it does, the `error_data` variable is set to the value of the `error` property.
Otherwise, the `error_data` variable is set to a default error message. The interceptor then returns
a rejected Promise with the `error_data` object as the reason for rejection. */
_private_server.interceptors.response.use(
  (response) => response,
  (error) => {
    let error_data = {
      code:0,
      message:'Hubo un error'
    }
    if(error?.response && error?.response?.data && error?.response?.data?.error){
      error_data = error?.response?.data?.error
    }
    //return Promise.reject((error?.response && error?.response?.data) || 'Something went wrong')
    return Promise.reject(error_data)
  }
);

export const private_server = _private_server
